/**
 * Read-only workbook viewer for phones, native, and any client without the
 * desktop-web spreadsheet editor.
 *
 * Renders `spreadsheetSpec` directly — the spec already ships inside the file
 * payload, so nothing here parses the .xlsx or hits the network. Cell display
 * comes from the shared spec module, so a value reads the same here as it does
 * in the desktop editor.
 */
import React, { useMemo, useState } from 'react';
import { FlatList, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isFormulaError } from '@/lib/spreadsheetFormulas';
import {
  ROW_HEADER_WIDTH,
  ROW_HEIGHT,
  cellTextAlign,
  columnLabel,
  columnPixelWidth,
  formatCellValue,
  normalizeWorkbookForViewing,
} from '@/constants/utils/spreadsheet_spec';

// Matches the desktop editor's header tint and selection wash, which are the
// accent color at low alpha.
const HEADER_TINT_ALPHA = '14';

export default function WorkbookViewer({ file }) {
  const { colors } = useTheme();
  const [sheetIndex, setSheetIndex] = useState(0);

  const workbook = useMemo(() => normalizeWorkbookForViewing(
    file?.parameters?.spreadsheetSpec,
    file?.parameters?.title || file?.name,
  ), [file]);

  const styles = useMemo(() => StyleSheet.create({
    container: { flex: 1, minWidth: 0, minHeight: 0 },
    headerCell: {
      height: ROW_HEIGHT,
      alignItems: 'center',
      justifyContent: 'center',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_secondary,
    },
    rowNumber: {
      width: ROW_HEADER_WIDTH,
      height: ROW_HEIGHT,
      alignItems: 'center',
      justifyContent: 'center',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_secondary,
    },
    cell: {
      height: ROW_HEIGHT,
      justifyContent: 'center',
      paddingHorizontal: 6,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    row: { flexDirection: 'row' },
    // The border lives on the wrapper, not the scroll content, so it spans the
    // viewport even when the tabs are narrower than the screen.
    tabBarWrapper: {
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
    },
    tabBar: {
      flexDirection: 'row',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    tab: {
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 7,
      backgroundColor: colors.solid_color_secondary,
    },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 32 },
  }), [colors]);

  if (!workbook.sheets.length) {
    return (
      <View style={styles.empty}>
        <Icon name="file-excel" size={30} color={colors.text_color_tertiary} />
        <Text color="secondary" style={{ textAlign: 'center' }}>This workbook has no sheets yet.</Text>
      </View>
    );
  }

  const activeIndex = Math.min(sheetIndex, workbook.sheets.length - 1);
  const sheet = workbook.sheets[activeIndex];
  const columnWidths = sheet.columns.map(columnPixelWidth);

  return (
    <View style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <View style={{ flex: 1 }}>
          <View style={styles.row}>
            <View style={styles.rowNumber} />
            {columnWidths.map((columnWidth, ci) => (
              <View key={`col-${ci}`} style={[styles.headerCell, { width: columnWidth }]}>
                <Text size="xs" color="tertiary">{columnLabel(ci)}</Text>
              </View>
            ))}
          </View>

          <FlatList
            key={sheet.id}
            data={sheet.rows}
            keyExtractor={(_, index) => String(index)}
            getItemLayout={(_, index) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index })}
            initialNumToRender={20}
            maxToRenderPerBatch={20}
            windowSize={5}
            renderItem={({ item: row, index: ri }) => {
              const isHeaderRow = sheet.headerRow && ri === 0;
              return (
                <View key={`row-${ri}`} style={styles.row}>
                  <View style={styles.rowNumber}>
                    <Text size="xs" color="tertiary">{ri + 1}</Text>
                  </View>
                  {row.map((cell, ci) => {
                    const isError = isFormulaError(cell?.v);
                    return (
                      <View
                        key={`cell-${ri}-${ci}`}
                        style={[
                          styles.cell,
                          { width: columnWidths[ci] },
                          isHeaderRow ? { backgroundColor: `${workbook.accent}${HEADER_TINT_ALPHA}` } : null,
                        ]}
                      >
                        <Text
                          size="s"
                          numberOfLines={1}
                          weight={isHeaderRow || cell?.b ? 'bold' : 'normal'}
                          style={[
                            { textAlign: cellTextAlign(cell) },
                            isError ? { color: colors.color_destructive } : null,
                          ]}
                        >
                          {formatCellValue(cell)}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              );
            }}
          />
        </View>
      </ScrollView>

      {workbook.sheets.length > 1 ? (
        <View style={styles.tabBarWrapper}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabBar}>
            {workbook.sheets.map((candidate, index) => (
              <Pressable
                key={candidate.id}
                accessibilityRole="tab"
                accessibilityState={{ selected: index === activeIndex }}
                onPress={() => setSheetIndex(index)}
                style={[
                  styles.tab,
                  index === activeIndex ? { backgroundColor: `${workbook.accent}${HEADER_TINT_ALPHA}` } : null,
                ]}
              >
                <Text
                  size="s"
                  weight={index === activeIndex ? 'semibold' : 'normal'}
                  color={index === activeIndex ? 'primary' : 'secondary'}
                >
                  {candidate.name}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}
